import { canMeasure } from './privacy.js';

export const MARKETING_CONSENT_KEY = 'tda_ad_measurement_consent_v2';
// Optional advertising SDKs are never loaded on the consultation request form.
const PUBLIC_LANDING_PATHS = new Set(['/', '/all-on-x', '/fixed-new-teeth', '/fixed-vs-removable-implant-teeth',
  '/dental-implants', '/dental-implant-cost', '/cosmetic-dentistry', '/invisalign', '/full-mouth-restoration',
  '/emergency-dentistry', '/general-dentistry', '/preventative-dentistry',
  '/sedation-dentistry', '/laser-dentistry', '/regenerative-dentistry',
  '/oral-surgery', '/periodontal-disease-treatment',
  '/cosmetic-dentist-trinity-fl', '/veneers-trinity-fl', '/teeth-whitening-trinity-fl', '/cosmetic-dentist-tampa-fl', '/veneers-palm-harbor-fl', '/invisalign-trinity-fl', '/invisalign-new-port-richey-fl', '/family-dentist-trinity-fl', '/spring-hill-fl', '/brooksville-fl', '/hudson-fl', '/wesley-chapel-fl', '/zephyrhills-fl', '/tampa-fl', '/clearwater-fl', '/st-petersburg-fl', '/all-on-4-vs-dentures', '/all-on-4-vs-all-on-6', '/dental-implant-cost-florida']);
const CAMPAIGN_KEYS = new Set(['utm_source', 'utm_medium', 'utm_campaign', 'utm_content',
  'gclid', 'gbraid', 'wbraid', 'fbclid', 'trkid', 'oppref']);
let loaded = false;

export function isMarketingLandingPath(path) {
  return PUBLIC_LANDING_PATHS.has(path.replace(/\/$/, '') || '/');
}

function hasMarketingConsent() {
  try {
    return window.location.origin === 'https://trinitydentalarts.com' &&
      navigator.globalPrivacyControl !== true && navigator.doNotTrack !== '1' &&
      localStorage.getItem('tda_cookie_consent_v2') === 'granted' &&
      localStorage.getItem(MARKETING_CONSENT_KEY) === 'granted';
  } catch { return false; }
}

export function canLoadMarketing() {
  try {
    if (!canMeasure() || window.location.origin !== 'https://trinitydentalarts.com' ||
        localStorage.getItem(MARKETING_CONSENT_KEY) !== 'granted' ||
        !isMarketingLandingPath(window.location.pathname)) return false;
    // Third-party SDKs can inspect the current URL themselves. An unknown query
    // or hash must never reach them, even though our own events are sanitized.
    for (const [key, value] of new URLSearchParams(window.location.search)) {
      if (!CAMPAIGN_KEYS.has(key)) return false;
      // OpenAI owns this opaque token; validate size without transforming it.
      if (key === 'oppref') {
        if (!value || value.length > 4096 || /[\s\x00-\x1f\x7f]/.test(value)) return false;
      } else if (!/^[a-z0-9_.-]{1,200}$/i.test(value)) return false;
    }
    if (window.location.hash && !['#consult-form', '#book', '#contact', '#consultation', '#service-next-step', '#quiz'].includes(window.location.hash)) return false;
    // Contact surfaces may mount asynchronously. Keep their privacy boundary
    // in place before optional SDK effects to prevent collection and reload loops.
    if (document.querySelector('[data-sensitive-contact-boundary]')) return false;
    return Boolean(document.querySelector('[data-consultation-quiz]')) &&
      !document.querySelector('form, input, textarea, [contenteditable="true"]');
  } catch { return false; }
}

function script(id, src, attributes = {}) {
  if (document.getElementById(id)) return;
  const el = document.createElement('script');
  el.id = id; el.src = src; el.async = true; el.referrerPolicy = 'no-referrer';
  for (const [name, value] of Object.entries(attributes)) el.setAttribute(name, value);
  document.head.appendChild(el);
}

export function syncMarketingConsent() {
  const allowed = canLoadMarketing();
  // Page exclusions are not consent revocations. The SDK deletes click
  // attribution cookies on consent(false), so send that only when permission
  // is actually withdrawn, while still disposing loaded SDKs on unsafe pages.
  if (allowed || !hasMarketingConsent()) window.oaiq?.('consent', allowed);
  if (!allowed) {
    // Removing a script element does not remove SDK listeners/timers. Dispose
    // of the whole document if a route changes after optional SDKs were loaded.
    if (loaded) window.location.reload();
    return false;
  }
  if (loaded) return true;
  loaded = true;

  window.oaiq = window.oaiq || function () { window.oaiq.q.push(arguments); };
  window.oaiq.q = window.oaiq.q || [];
  window.oaiq('consent', true);
  window.oaiq('init', { pixelId: '5dVmTEEjZiURc1QETLRsGt' });
  window.oaiq('measure', 'page_viewed', { type: 'contents' }, { opt_out: true });
  script('tda-openai-pixel', 'https://bzrcdn.openai.com/sdk/oaiq.min.js');

  window.vbpx = window.vbpx || function () {
    window.vbpx.process ? window.vbpx.process.apply(window.vbpx, arguments) : window.vbpx.queue.push(arguments);
  };
  window.vbpx.queue = window.vbpx.queue || [];
  window.vbpx.b = Date.now();
  window.vbpx('init', 'x4EOdA');
  window.vbpx('event', 'page_view');
  script('tda-vibe-pixel', 'https://s.vibe.co/vbpx.js');

  // Meta browser tracking is intentionally absent. Healthcare conversion
  // data stays out of Meta; a server integration is not a policy exemption.

  // RetargetIQ is no longer part of this gated set: by owner decision
  // (Nov 3, 2026) it runs unconditionally from /retargetiq-loader.js, which
  // index.html references with `defer`. Its session-history keys stay intact.
  script('growdental-tracking', 'https://link.growdental.ai/js/external-tracking.js', {
    'data-tracking-id': 'tk_6a8cf80b18f84ff5bfc68a3750071de4',
  });
  return true;
}

export function installMarketingNavigationBoundary() {
  // A real navigation disposes of every vendor's timers and form listeners.
  // Anchor jumps within the current public page can remain in place.
  const onClick = event => {
    if (!loaded || event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
    const link = event.target?.closest?.('a[href]');
    if (!link || link.target === '_blank') return;
    try {
      const url = new URL(link.href, window.location.href);
      if (url.origin === window.location.origin &&
          (url.pathname !== window.location.pathname || url.search !== window.location.search)) {
        event.preventDefault();
        window.location.assign(url.href);
      }
    } catch { /* Ignore non-navigation links. */ }
  };
  document.addEventListener('click', onClick, true);
  return () => document.removeEventListener('click', onClick, true);
}
